import { useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@verlo/shared/components/ui/dialog';

/**
 * A void, confirmed by someone who may void.
 *
 * A server's void goes through the manager-PIN dialog. A manager holds `epos.void` and needs
 * no PIN — but a void still prints a slip on the pass and takes money off the bill, and the
 * bin is a small target next to + and −. So one moment to say "yes, that one", naming the
 * dish, rather than a void on a stray tap.
 */
const VoidConfirmDialog = ({ open, onOpenChange, label, where = 'the kitchen', onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e) {
      // Kept in the dialog rather than a toast: the operator is looking here, and the
      // pad behind is unchanged until the server has the void.
      setError(e?.response?.data?.detail || 'That void did not go through. Nothing has changed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { setError(null); onOpenChange(v); } }}>
      <DialogContent size="confirm" data-testid="void-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Void {label}?</DialogTitle>
          <DialogDescription>
            It has gone to {where}. They will be told straight away, and it comes off the bill.
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-signal-stop" role="alert">{typeof error === 'string' ? error : 'That void did not go through.'}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Keep it</Button>
          <Button onClick={confirm} disabled={busy} className="bg-signal-stop hover:bg-signal-stop"
                  data-testid="void-confirm-btn">
            {busy ? 'Voiding…' : 'Void'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VoidConfirmDialog;
